import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { LazyResultsGrid } from "./LazyResultsGrid";
import { Customer } from "../shared/interfaces/Customer";
import { DataTablePFSEvent } from "primereact/datatable";
import { usePagedResultsList } from "../data/usePagedResultsList";
import { ResultListRequestType } from "../shared/apiHandlers/ResultsAPIs";
import { ReportValues } from "../shared/interfaces/ReportingPayload";
import { ReportModalData } from "./ReportModalData";

interface Props {
  lazyState: DataTablePFSEvent;
  requestType: ResultListRequestType | undefined;
  companyId: number;
  siteId: number;
  selectedCustomer: Customer | undefined;
  reportModalData: ReportModalData[];
  reportValuesList: ReportValues[];
  matchingReportModalData: ReportModalData[];
  matchingReportValuesList: ReportValues[];
  onFilter: (event: DataTablePFSEvent) => void;
  onSort: (event: DataTablePFSEvent) => void;
  onPage: (event: DataTablePFSEvent) => void;
  handleUploadButtonClick: () => void;
  onGenerateAppendixSuccess: () => void;
  onGenerateAppendixError: () => void;
  setReportModalData: (reportModalData: ReportModalData[]) => void;
  setReportValuesList: (reportValuesList: ReportValues[]) => void;
  setMatchingReportModalData: (matchingReportModalData: ReportModalData[]) => void;
  setMatchingReportValuesList: (matchingReportValuesList: ReportValues[]) => void;
  handleUpdateReportStatusSuccess: () => void;
  handleUpdateReportStatusError: () => void;
}

export function AllResults(props: Props) {
  const { t } = useTranslation();

  const [errorToast, setErrorToast] = useState({
    show: false,
    mode: ToastMode.Error,
    text: "",
  });

  const results = usePagedResultsList(props.lazyState, props.requestType, props.companyId, props.siteId, undefined);

  useEffect(() => {
    if (results.error) {
      setErrorToast({
        text:
          results.error.status === 403
            ? t("common.permissionError")
            : t("common.dataFetchError", { val: "$t(results.results)" }),
        mode: ToastMode.Error,
        show: true,
      });
    } else {
      setErrorToast({
        text: "",
        mode: ToastMode.Error,
        show: false,
      });
    }
  }, [results?.error, t]);

  return (
    <React.Fragment>
      {errorToast.show ? (
        <div className="w-100 d-flex justify-content-center pt-2">
          <MessageToast text={errorToast.text} mode={errorToast.mode} show={errorToast.show}></MessageToast>
        </div>
      ) : (
        (results?.data || results?.isLoading) &&
        props.selectedCustomer && (
          <>
            <div className="pt-3">
              <LazyResultsGrid
                resultData={results?.data}
                resultDataLoading={results.isLoading}
                resultDataFetching={results.isFetching}
                customerId={props.selectedCustomer.id}
                companyId={props.companyId}
                siteId={props.siteId}
                lazyState={props.lazyState}
                reportModalData={props.reportModalData}
                reportValuesList={props.reportValuesList}
                matchingReportModalData={props.matchingReportModalData}
                matchingReportValuesList={props.matchingReportValuesList}
                onFilter={props.onFilter}
                onSort={props.onSort}
                onPage={props.onPage}
                onDataRefreshClick={results.refetch}
                onUploadButtonClick={props.handleUploadButtonClick}
                onGenerateAppendixSuccess={props.onGenerateAppendixSuccess}
                onGenerateAppendixError={props.onGenerateAppendixError}
                setReportModalData={props.setReportModalData}
                setReportValuesList={props.setReportValuesList}
                setMatchingReportModalData={props.setMatchingReportModalData}
                setMatchingReportValuesList={props.setMatchingReportValuesList}
                refetchResults={results.refetch}
                handleUpdateReportStatusSuccess={props.handleUpdateReportStatusSuccess}
                handleUpdateReportStatusError={props.handleUpdateReportStatusError}
              />
            </div>
          </>
        )
      )}
    </React.Fragment>
  );
}
